import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { useCriarOportunidade, useSalvarRascunho } from '../hooks';
import {
  buildCreatePayload,
  OpportunityForm,
  OpportunityFormState,
} from '../components/OpportunityForm';
import { OportunidadeDuplicataError, OportunidadeDuplicataCandidate } from '../api';

/**
 * Página `/oportunidades/nova` — criação de oportunidade
 * (US-COM-004 §Cadastro Base). Aceita:
 *   - Salvar rascunho: POST /opportunities/drafts, sem validação de
 *     pré-condições (fluxo privado do autor)
 *   - Efetivar: POST /opportunities. 409 c/ candidato duplicado abre
 *     modal de confirmação; usuário reenvia com
 *     `confirmedNotDuplicate=true`
 */
export default function NovaOportunidadePage(): React.ReactElement {
  const navigate = useNavigate();
  const criar = useCriarOportunidade();
  const rascunho = useSalvarRascunho();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicata, setDuplicata] = useState<OportunidadeDuplicataCandidate | null>(null);
  const [pendingState, setPendingState] = useState<OpportunityFormState | null>(null);

  const submitEfetivacao = async (state: OpportunityFormState, force = false) => {
    setSubmitError(null);
    try {
      const payload = buildCreatePayload(state);
      const created = await criar.mutateAsync({
        ...payload,
        confirmedNotDuplicate: force || undefined,
      });
      navigate(`/oportunidades/${created.id}`);
    } catch (err) {
      if (err instanceof OportunidadeDuplicataError) {
        setDuplicata(err.candidate);
        setPendingState(state);
        return;
      }
      setSubmitError(getFriendlyErrorMessage(err, 'Falha ao cadastrar oportunidade.'));
    }
  };

  const submitRascunho = async (state: OpportunityFormState) => {
    setSubmitError(null);
    try {
      const payload = buildCreatePayload(state);
      const created = await rascunho.mutateAsync(payload);
      navigate(`/oportunidades/${created.id}`);
    } catch (err) {
      setSubmitError(getFriendlyErrorMessage(err, 'Falha ao salvar rascunho.'));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <button
        onClick={() => navigate('/oportunidades')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Voltar para a lista
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Nova oportunidade</h1>
        <p className="mt-1 text-sm text-gray-600">
          Preencha os dados essenciais para efetivar, ou salve como rascunho para
          continuar depois.
        </p>
      </div>

      {duplicata && pendingState && (
        <Alert variant="warning" className="mb-4">
          <div className="space-y-2">
            <div>
              <strong>Já existe oportunidade aberta similar</strong> para este cliente com
              o mesmo produto/serviço:{' '}
              <span className="font-mono text-xs">
                {duplicata.codigo ?? '—'} • {duplicata.nome}
              </span>
              . Você quer prosseguir mesmo assim?
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => navigate(`/oportunidades/${duplicata.id}`)}
                className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-50"
              >
                Abrir a existente
              </button>
              <button
                type="button"
                onClick={() => {
                  const s = pendingState;
                  setDuplicata(null);
                  setPendingState(null);
                  void submitEfetivacao(s, true);
                }}
                className="rounded-md bg-amber-600 px-3 py-1 text-xs font-medium text-white hover:bg-amber-700"
              >
                Sim, criar nova
              </button>
              <button
                type="button"
                onClick={() => {
                  setDuplicata(null);
                  setPendingState(null);
                }}
                className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Alert>
      )}

      <OpportunityForm
        onSubmit={(s) => submitEfetivacao(s)}
        onSaveDraft={submitRascunho}
        onCancel={() => navigate('/oportunidades')}
        submitLabel="Efetivar oportunidade"
        isSubmitting={criar.isPending}
        isSavingDraft={rascunho.isPending}
        submitError={submitError}
      />
    </div>
  );
}
